// The TV display's slideshow (pages/TvDisplays/Show): which slides to
// show, in what order, and for how long. The stage is a fixed 1920×1080,
// so each slide holds a known number of rows; longer lists split into
// even pages (7 classes → 4 + 3, never 6 + 1) that rotate like any other
// slide.
import { campaignPhase, formatPhaseDate, formatPhaseTime } from "@/lib/campaignPhase";
import { formatMoney } from "@/lib/format";
import { formatPrizeAmount } from "@/lib/prizes";

export const REWARDS_PER_SLIDE = 5;
export const CLASSES_PER_SLIDE = 6;

const SECONDS = { goal: 15, rewards: 12, readers: 15, classes: 15 };

// A TV display code as typed ("k7mx-4qpa") → "K7MX4QPA", and shown in
// two groups of four ("K7MX-4QPA"). Mirrors School.normalize_display_code
// and School.format_display_code.
export function normalizeTvCode(input) {
  return String(input || "").toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 8);
}

export function formatTvCode(code) {
  return (String(code || "").match(/.{1,4}/g) || []).join("-");
}

// Every part on: the default, and what the settings preview starts from.
const ALL_PARTS = { goal: true, rewards: true, readers: true, classes: true, donate_link: true };

export function buildSlides({ rewards, top_readers, classrooms, parts = ALL_PARTS }) {
  const goal = { key: "goal", type: "goal" };
  const slides = parts.goal ? [goal] : [];

  if (parts.rewards) {
    evenPages(rewards, REWARDS_PER_SLIDE).forEach((items, i, pages) => {
      slides.push({ key: `rewards-${i}`, type: "rewards", items, page: i + 1, pages: pages.length });
    });
  }
  if (parts.readers && top_readers.length > 0) slides.push({ key: "readers", type: "readers" });
  // Before anyone has logged minutes every class would read "No minutes yet".
  if (parts.classes && classrooms.some((c) => c.leader_count > 0)) {
    evenPages(classrooms, CLASSES_PER_SLIDE).forEach((items, i, pages) => {
      slides.push({ key: `classes-${i}`, type: "classes", items, page: i + 1, pages: pages.length });
    });
  }
  // The chosen slides have nothing in them yet (only top readers, before
  // anyone has read): the goal slide rather than a blank screen.
  if (slides.length === 0) slides.push(goal);

  return slides.map((slide) => ({ ...slide, seconds: SECONDS[slide.type] }));
}

export function evenPages(items, max) {
  if (items.length === 0) return [];
  const pages = Math.ceil(items.length / max);
  const size = Math.ceil(items.length / pages);
  return Array.from({ length: pages }, (_, i) => items.slice(i * size, (i + 1) * size));
}

// Where the read-a-thon is, in a line under the school name. Dates are
// in the school's timezone: a TV's own clock is often left on UTC.
export function phaseLine(campaign, now = new Date()) {
  const zone = { timeZone: campaign.timezone };
  const date = (iso) => formatPhaseDate(iso, zone);
  switch (campaignPhase(campaign, now)) {
    case "donations_open":
    case "getting_ready":
      return `Reading starts ${date(campaign.start_at)}`;
    case "reading":
      if (campaign.days_remaining <= 1) return `Day ${campaign.current_day} of ${campaign.days_total} · Last day!`;
      return `Day ${campaign.current_day} of ${campaign.days_total} · ${campaign.days_remaining} days left`;
    case "catch_up":
      return `Reading is done · log last minutes by ${formatPhaseTime(campaign.minutes_close_at, zone)}`;
    case "wrapping_up":
      return `Reading is done · donations close ${date(campaign.grace_ends_at)}`;
    default:
      return "Final results";
  }
}

// Whole dollars read better across a room. Rounded down, so the screen
// never claims more than was raised.
export function tvMoney(cents) {
  return formatMoney(Math.floor((Number(cents) || 0) / 100) * 100);
}

// What's left before a reward unlocks, rounded up to whole dollars.
export function amountToGo(reward) {
  const left = Math.max(0, reward.threshold_amount - reward.current_amount);
  return reward.kind === "money" ? formatMoney(Math.ceil(left / 100) * 100) : formatPrizeAmount(reward.kind, left);
}

// The locked reward closest to unlocking, for the goal slide.
export function nextReward(rewards) {
  return rewards
    .filter((r) => !r.unlocked)
    .reduce((best, r) => (!best || r.progress_pct > best.progress_pct ? r : best), null);
}
